import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Lock, User, Shield, ArrowLeft, Eye, EyeOff } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';

export default function Login() {
  const { login, mfaPending, verifyMfa, cancelMfa } = useAuth();
  const { colorTheme, cycleColorTheme } = useTheme();
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [totpCode, setTotpCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [shakeError, setShakeError] = useState(false);

  const triggerShake = () => {
    setShakeError(true);
    setTimeout(() => setShakeError(false), 500);
  };

  const handleSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const result = await login(username, password);
      if (result?.mfa_required) {
        // MFA step shows automatically
      }
    } catch {
      setError('Invalid username or password');
      triggerShake();
    } finally {
      setLoading(false);
    }
  };

  const handleMfaSubmit = async e => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await verifyMfa(totpCode);
    } catch (err) {
      setError(err.response?.data?.error || 'Invalid authenticator code');
      triggerShake();
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="login-wrapper login-split">
      {/* Theme toggle */}
      <motion.button
        type="button"
        className="login-theme-toggle"
        onClick={cycleColorTheme}
        title="Toggle theme"
        whileHover={{ scale: 1.1 }}
        whileTap={{ scale: 0.9 }}
      >
        {colorTheme === 'light' ? '🌙' : '☀️'}
      </motion.button>

      {/* LEFT PANEL: Branding */}
      <div className="login-brand-panel">
        <div className="login-brand-inner">
          <motion.img
            src="/logo.png"
            alt="Vision Care Services Private Limited"
            className="login-brand-logo"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          />
        </div>
      </div>

      {/* RIGHT PANEL: Login Form */}
      <div className="login-form-panel">
        <motion.div
          className={`login-card ${shakeError ? 'animate-shake' : ''}`}
          initial={{ opacity: 0, y: 20, scale: 0.96 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
        >
          {/* Header */}
          <div className="login-header">
            <h1>Welcome back</h1>
            <p className="login-subtitle">
              {mfaPending ? 'Two-factor authentication' : 'Sign in to your account'}
            </p>
          </div>

          <AnimatePresence mode="wait">
            {!mfaPending ? (
              <motion.form
                key="login-form"
                onSubmit={handleSubmit}
                className="login-form"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ duration: 0.3 }}
              >
                {error && (
                  <motion.div
                    className="login-error"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                    exit={{ opacity: 0, height: 0 }}
                  >
                    {error}
                  </motion.div>
                )}

                <div className="login-field">
                  <label htmlFor="login-username">
                    <User size={14} />
                    Username
                  </label>
                  <input
                    id="login-username"
                    type="text"
                    value={username}
                    onChange={e => setUsername(e.target.value)}
                    placeholder="Enter your username"
                    autoFocus
                    autoComplete="username"
                    required
                  />
                </div>

                <div className="login-field">
                  <label htmlFor="login-password">
                    <Lock size={14} />
                    Password
                  </label>
                  <div className="login-password-wrap">
                    <input
                      id="login-password"
                      type={showPassword ? 'text' : 'password'}
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      placeholder="Enter your password"
                      autoComplete="current-password"
                      required
                    />
                    <button
                      type="button"
                      className="login-password-toggle"
                      onClick={() => setShowPassword(v => !v)}
                      tabIndex={-1}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                    >
                      {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>

                <motion.button
                  type="submit"
                  className="login-submit"
                  disabled={loading}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading ? (
                    <span className="login-spinner" />
                  ) : (
                    'Sign In'
                  )}
                </motion.button>
              </motion.form>
            ) : (
              <motion.form
                key="mfa-form"
                onSubmit={handleMfaSubmit}
                className="login-form"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {error && (
                  <motion.div
                    className="login-error"
                    initial={{ opacity: 0, height: 0 }}
                    animate={{ opacity: 1, height: 'auto' }}
                  >
                    {error}
                  </motion.div>
                )}

                <div className="mfa-icon-wrap">
                  <motion.div
                    initial={{ rotateY: 180, opacity: 0 }}
                    animate={{ rotateY: 0, opacity: 1 }}
                    transition={{ duration: 0.5, ease: 'easeOut' }}
                  >
                    🔐
                  </motion.div>
                </div>

                <p className="mfa-description">
                  Open Google Authenticator and enter the 6-digit code for{' '}
                  <strong>{mfaPending.username}</strong>
                </p>

                <div className="login-field">
                  <label htmlFor="totp-input">
                    <Shield size={14} />
                    Authenticator Code
                  </label>
                  <input
                    id="totp-input"
                    value={totpCode}
                    onChange={e => setTotpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    autoFocus
                    required
                    className="totp-input"
                    maxLength={6}
                    autoComplete="one-time-code"
                    inputMode="numeric"
                  />
                </div>

                <motion.button
                  type="submit"
                  className="login-submit"
                  disabled={loading || totpCode.length < 6}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {loading ? <span className="login-spinner" /> : 'Verify Code'}
                </motion.button>

                <button
                  type="button"
                  className="login-back-btn"
                  onClick={() => { cancelMfa(); setError(''); setTotpCode(''); }}
                >
                  <ArrowLeft size={14} />
                  Back to Login
                </button>
              </motion.form>
            )}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
}
